import { useCMSParams, useQueryCMS } from '@/features/cms/services/CMS/queries'
import { getErrorStatus } from '@/features/shared/utils/dataFetching/utils'
import { ErrorPage } from '@/features/shared/components/ErrorPage'
import { LoadingIndicator } from '@shipt/design-system-loading'
import { Row } from '@shipt/design-system-layouts'
import { useIsPage } from '@/features/shared/hooks/useIsPage'
import { LayoutRenderer } from '@/features/cms/components/CMS/components/Layouts'
import { usePathname } from 'next/navigation'
import { layoutEagerComponents } from '@/features/cms/components/CMS/components/Layouts/LayoutEagerComponents'
import { SEOHead } from '@/features/shared/components/SEOHeader/SEOHead'
import { getCanonicalUrl } from '@/features/shared/utils/seo/seoConfig'
import { buildCmsStructuredData } from '@/features/shared/utils/seo/buildCmsStructuredData'

type Props = {
  /**
   * CMS has no concept of retailer centric urls. All content is mounted on a slug without retailer in the path
   * This prop is passed from the LandingPage SSR handler, which strips the retailer from the string and passes in a
   * retailer agnostic slug that the CMS understands. Blindly passing through the slug with retailer in the string will
   * result in all retailer landing pages to 404 due to CMS not knowing how to resolve this page content
   */
  retailerAgnosticSlug?: string
  isBot?: boolean
}
export const CMSLandingPage = ({ retailerAgnosticSlug, isBot }: Props) => {
  const pathname = usePathname() ?? ''
  const canonicalUrl = getCanonicalUrl(pathname)
  const slug = retailerAgnosticSlug ?? pathname.slice(1)
  const cmsParams = useCMSParams(slug)
  const { GLOBAL_LANDING_PAGE: isGlobalPage } = useIsPage()
  const { data, isPending, error } = useQueryCMS({
    params: cmsParams,
    enabled: isGlobalPage
      ? Boolean(cmsParams.addressParams?.zip)
      : Boolean(cmsParams.storeParams.store_location_id),
    keepPreviousData: isGlobalPage,
    enableDeferredHydration: !isBot,
  })

  if (isPending) {
    return (
      <Row align="center" justify="center" marginTop="5xl">
        <LoadingIndicator size="xl" />
      </Row>
    )
  }
  if (error) {
    return <ErrorPage statusCode={getErrorStatus(error)} isCMSPage />
  }
  return (
    <>
      <SEOHead
        title={data.seo_title}
        description={data.seo_description}
        canonicalUrl={canonicalUrl}
        doNotIndex={data.do_not_index}
        doNotFollow={data.do_not_follow}
        additionalSchemas={buildCmsStructuredData({
          cmsData: data,
          canonicalUrl,
        })}
      />
      <LayoutRenderer layoutData={data.content} layoutEagerComponents={layoutEagerComponents} />
    </>
  )
}
